import { supabase } from '../../lib/supabase';
import { validateFileType } from '../../utils/fileUtils';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export async function uploadFile(file: File, bucket: string, path: string): Promise<{ url: string }> {
  let retryCount = 0;

  try {
    // Validate auth session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication required');
    }

    // Sanitize and validate path
    const filePath = path.replace(/\/{2,}/g, '/').replace(/^\//, '');

    while (retryCount < MAX_RETRIES) {
      try {
        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type
          });

        if (!uploadError) {
          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(filePath);

          return { url: publicUrl };
        }

        // Handle specific errors
        if (uploadError.message?.includes('storage quota')) {
          throw new Error('Storage quota exceeded');
        }
        if (uploadError.message?.includes('permission denied') || uploadError.message?.includes('row-level security')) {
          throw new Error('Permission denied - please ensure you have admin access');
        }
        if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('Upload service not available');
        }

        // Retry on other errors
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
          continue;
        }

        throw uploadError;
      } catch (error) {
        if (retryCount >= MAX_RETRIES - 1) {
          throw error;
        }
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
      }
    }

    throw new Error('Upload failed after multiple retries');
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
}